<script lang="ts">
    import type {SegmentedColumnChartDataColumn} from 'shared/components/kp-charts/types';
    import type {CssSize} from 'shared/ui-widgets/types';
    import {tweened} from 'svelte/motion';
    import {cubicOut} from 'svelte/easing';
    import {onMount} from 'svelte';
    import {range} from 'shared/utils/array-utils';

    export let data: SegmentedColumnChartDataColumn[];
    export let height: CssSize;
    export let horizontalLabelLinesCount = 10;

    const maxColumnValue = getMaxColumnValue();

    const progressTween = tweened(0, {
        duration: 1000,
        easing: cubicOut
    });

    onMount(() => {
        progressTween.set(1);
    });

    function getMaxColumnValue(): number {
        return data.reduce((max, column) => {
            const columnValue = column.segments.reduce((sum, segment) => sum + segment.value, 0);
            return Math.max(max, columnValue);
        }, 0);
    }

    function getColumnValue(column: SegmentedColumnChartDataColumn): number {
        return column.segments.reduce((sum, segment) => sum + segment.value, 0);
    }
</script>

<div class="kp-segmented-column-chart-container" style:--chart-height="{height}">
    {#if horizontalLabelLinesCount > 0}
        <div class="horizontal-label-lines-container"
             style:--horizontal-label-lines-count="{horizontalLabelLinesCount}">
            {#each range(0, horizontalLabelLinesCount - 1) as labelLineIndex}
                <div class="horizontal-label-line" style:--line-index="{labelLineIndex}"></div>
            {/each}
        </div>
    {/if}

    <div class="segmented-column-chart">
        {#each data as column}
            {@const columnValue = getColumnValue(column)}

            <div class="chart-column" style:--column-height-ratio="{columnValue / maxColumnValue * $progressTween}">
                {#each column.segments as segment}
                    <div class="column-segment"
                         style:height="{segment.value / columnValue * 100}%"
                         style:background-color="{segment.color}">
                    </div>
                {/each}

                <!--<span class="column-label">{column.label}</span>-->
            </div>
        {/each}
    </div>
</div>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    @labels-font-size: 10px;

    .kp-segmented-column-chart-container {
        position: relative;
        display: flex;
        width: 100%;
        gap: @spacing-xxl;

        .horizontal-label-lines-container {
            .horizontal-label-line {
                position: absolute;
                left: 0;
                right: 0;
                bottom: calc(100% / var(--horizontal-label-lines-count) * var(--line-index));
                height: 1px;
                background-image: repeating-linear-gradient(to right, @themed-border-default 0 10px, transparent 10px 20px);
                opacity: 0.5;
            }
        }

        .segmented-column-chart {
            display: flex;
            gap: @spacing-m;
            align-items: flex-end;
            flex: 1;
            height: var(--chart-height);

            .chart-column {
                flex: 1;
                width: 100%;
                height: calc(var(--chart-height) * var(--column-height-ratio));
                display: flex;
                flex-direction: column;
                overflow: hidden;
                border: 1px solid rgba(0, 0, 0, 0.2);
                border-top-left-radius: @border-radius-default;
                border-top-right-radius: @border-radius-default;

                .column-segment {
                    width: 100%;
                }
            }
        }
    }
</style>